import React, { useState, useCallback, useEffect } from 'react';
import Map from './index';
import FieldSelector from '../Controls/FieldSelector';
import InstrumentSelector from '../Controls/InstrumentSelector';
import TimePointSelector from '../Controls/TimePointSelector';
import LayerList from '../Controls/LayerList';
import AIULegend from '../Controls/AIULegend';
import { FieldNumber, InstrumentType, RasterLayer, TimePoint, Layer } from '../../types';
import { rasterLayers, getLayersByField, loadLayers, getLayersByField as getUnifiedLayersByField } from '../Layers/index';
import { Map as LeafletMap } from 'leaflet'; // Import Leaflet Map type


const MapContainer: React.FC = () => {
  const FIELDCENTER: [number, number] = [-96.858249, 36.354374];

  const [viewState, setViewState] = useState({
    longitude: FIELDCENTER[0],
    latitude: FIELDCENTER[1],
    zoom: 19
  });

  const [mapInstance, setMapInstance] = useState<LeafletMap | null>(null);

  // State for layer system
  const [layers, setLayers] = useState<Layer[]>([]);
  const [loadingLayers, setLoadingLayers] = useState<boolean>(true);

  // State for layer filters - show all layers in list, but enable RGB PostBurial by default
  const [selectedField, setSelectedField] = useState<FieldNumber>(1);
  const [selectedInstrument, setSelectedInstrument] = useState<InstrumentType | 'All'>('All');
  const [selectedTimePoint, setSelectedTimePoint] = useState<TimePoint | 'All'>('All');

  const getDefaultVisibleLayers = (allLayers: Layer[]): Layer[] => {
    // Prioritize RGB PostBurial layers for default selection
    const prioritizedLayers = allLayers.filter(layer => {
      const sensorType = layer.sensorType.toLowerCase();
      const burialTime = layer.burialTime.toLowerCase();
      const isRGB = sensorType.includes('rgb') || sensorType.includes('camera');
      const isPostBurial = burialTime.includes('postburial') || burialTime.includes('post-burial');
      return isRGB && isPostBurial;
    });

    // If no RGB PostBurial layers, fall back to any available layers
    const fallbackLayers = prioritizedLayers.length > 0 ? prioritizedLayers : allLayers;

    // Get one layer per field, enabling both imagery and detections
    const defaultLayers = fallbackLayers
      .reduce((acc: Layer[], layer) => {
        // Only add if we don't already have a layer for this field
        if (!acc.some(l => l.field === layer.field)) {
          acc.push({
            ...layer,
            showImagery: layer.hasImagery,
            showDetections: layer.hasDetections
          });
        }
        return acc;
      }, []);

    return defaultLayers;
  };

  // Add state for mouse coordinates
  const [mouseCoordinates, setMouseCoordinates] = useState<{ lat: number; lng: number } | null>(null);

  // State for AIU legend visibility
  const [showAIULegend, setShowAIULegend] = useState<boolean>(true);

  // Load layers from datasets.csv on component mount
  useEffect(() => {
    const initializeLayers = async () => {
      try {
        setLoadingLayers(true);
        const loadedLayers = await loadLayers();

        // Apply default selections to prioritize RGB PostBurial layers
        const defaultLayers = getDefaultVisibleLayers(loadedLayers);

        // Update layers with default selections
        const layersWithDefaults = loadedLayers.map(layer => {
          const defaultLayer = defaultLayers.find(dl => dl.id === layer.id);
          if (defaultLayer) {
            return defaultLayer;
          }
          return layer;
        });

        setLayers(layersWithDefaults);
        console.log('Loaded layers with RGB PostBurial defaults:', layersWithDefaults);
      } catch (error) {
        console.error('Failed to load layers:', error);
      } finally {
        setLoadingLayers(false);
      }
    };

    initializeLayers();
  }, []);

  // Toggle imagery visibility for a layer - only one can be active at a time
  const toggleImagery = (layerId: string) => {
    console.log('Toggling imagery for layerID:', layerId);
    setLayers(prev =>
      prev.map(layer => {
        if (layer.id === layerId) {
          // Toggle the selected layer
          return { ...layer, showImagery: !layer.showImagery };
        } else {
          // Turn off imagery for all other layers
          return { ...layer, showImagery: false };
        }
      })
    );
  };

  // Toggle detections visibility for a layer - only one can be active at a time
  const toggleDetections = (layerId: string) => {
    console.log('Toggling detections for layerID:', layerId);
    setLayers(prev =>
      prev.map(layer => {
        if (layer.id === layerId) {
          // Toggle the selected layer
          return { ...layer, showDetections: !layer.showDetections };
        } else {
          // Turn off detections for all other layers
          return { ...layer, showDetections: false };
        }
      })
    );
  };

  // Filter layers based on current selections
  const filteredLayers = React.useMemo(() => {
    let filteredLayers = getUnifiedLayersByField(layers, selectedField);

    if (selectedInstrument !== 'All') {
      filteredLayers = filteredLayers.filter(layer => {
        const sensorType = layer.sensorType.toLowerCase();
        switch (selectedInstrument) {
          case 'Ortho':
            return sensorType.includes('rgb') || sensorType.includes('camera');
          case 'TIR':
            return sensorType.includes('thermal') || sensorType.includes('tir') || sensorType.includes('lwir');
          case 'Spectral':
            return sensorType.includes('multispectral') || sensorType.includes('hyperspectral') || sensorType.includes('spectral');
          default:
            return true;
        }
      });
    }

    if (selectedTimePoint !== 'All') {
      filteredLayers = filteredLayers.filter(layer => {
        const burialTime = layer.burialTime.toLowerCase();
        switch (selectedTimePoint) {
          case 'PreBurial':
            return burialTime.includes('preburial') || burialTime.includes('pre-burial');
          case 'PostBurial':
            return burialTime.includes('postburial') || burialTime.includes('post-burial');
          default:
            return true;
        }
      });
    }

    return filteredLayers;
  }, [layers, selectedField, selectedInstrument, selectedTimePoint]);

  // Check if any detections are visible to show/hide legend
  const hasVisibleDetections = React.useMemo(() => {
    return layers.some(layer => layer.showDetections && layer.hasDetections);
  }, [layers]);

  if (loadingLayers) {
    return (
      <div className="map-container">
        <div className="loading-message">
          Loading layers from datasets.csv...
        </div>
      </div>
    );
  }

  return (
    <div className="map-container">
      <Map
        viewState={viewState}
        setViewState={setViewState}
        mapInstance={mapInstance}
        setMapInstance={setMapInstance}
        layers={layers}
        selectedField={selectedField}
        FIELDCENTER={FIELDCENTER}
        // Pass coordinate state and setter down
        mouseCoordinates={mouseCoordinates}
        setMouseCoordinates={setMouseCoordinates}
      />

      <div className="controls-panel">
        <FieldSelector
          selectedField={selectedField}
          setSelectedField={setSelectedField}
          mapInstance={mapInstance}
        />

        <LayerList
          filteredLayers={filteredLayers}
          toggleImagery={toggleImagery}
          toggleDetections={toggleDetections}
        />
      </div>

      {hasVisibleDetections && (
        <div className="legend-panel">
          <AIULegend
            visible={showAIULegend}
            onToggleVisibility={() => setShowAIULegend(!showAIULegend)}
            showInterpretations={true}
          />
        </div>
      )}
    </div>
  );
};

export default MapContainer;
